import { apiClient } from '@littlebird/auth-client';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { reaction } from 'mobx';
import { useSyncExternalStore } from 'react';

import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { userStore } from '@/stores/user.store';

export type ModelCompany =
  | 'anthropic'
  | 'openai'
  | 'google'
  | 'zai'
  | 'moonshot'
  | 'deepseek'
  | 'xai'
  | 'meta';

// Users, or Littlebird's own team
export type AgentAudience = 'users' | 'internal';

export type AgentPickerEntry = {
  agent: string;
  // The decorated alias the dropdown listed before; a saved selection may still hold it
  alias: string | null;
  name: string;
  version: string | null;
  company: ModelCompany | null;
  // What the agent serves today, e.g. "Default · 65%" or "Proactive"
  role: string | null;
  // The widest audience the agent serves a chat mode for, if any
  audience: AgentAudience | null;
};

type AgentPickerResponse = { agents: AgentPickerEntry[] };

// Agents and rollout shares change only with a backend deploy. Only the
// Default shares show in the chat modes, so a Max or Ultra share change is
// picked up on the first focus after this long
const AGENT_PICKER_STALE_MS = 5 * 60 * 1000;
const NO_MODES: string[] = [];

const subscribeToAllowedModes = (onStoreChange: () => void) =>
  reaction(() => userStore.user?.allowed_chat_modes, onStoreChange);

const getAllowedModes = () => userStore.user?.allowed_chat_modes ?? NO_MODES;

export const useAgentPickerQuery = () => {
  const isInternalUser = useIsInternalUser();
  // A deploy that changes a rollout share renames that agent's alias in the
  // account's chat modes, so the entries are refetched with them
  const allowedModes = useSyncExternalStore(
    subscribeToAllowedModes,
    getAllowedModes
  );
  return useQuery({
    queryKey: ['agent-config', 'picker', allowedModes],
    queryFn: async () =>
      (await apiClient.get<AgentPickerResponse>('/agent_config/picker')).agents,
    enabled: isInternalUser,
    staleTime: AGENT_PICKER_STALE_MS,
    placeholderData: keepPreviousData,
  });
};
